import { Alert, AlertTitle, Typography } from '@mui/material'
import Debug from 'debug'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { UseQueryExecute, useQuery } from 'urql'
import { NotFound } from '../components/NotFound'
import { graphql } from '../gql'
import { OrderContextProviderQuery } from '../gql/graphql'

const log = Debug('ela:OrderContextProvider')
log.log = console.info.bind(console)

export type TOrderContext = {
  fetching: boolean
  error?: Error
  refetch: UseQueryExecute
  rfq?: OrderContextProviderQuery['Rfq']
  dialogOpen: 'addPayment' | 'editPayment' | undefined
  editingPayment?: NonNullable<NonNullable<OrderContextProviderQuery['Rfq']['quote']>['purchaseOrder']>['payments'][0] // The payment being edited
  openPaymentDialog: () => void
  openEditPaymentDialog: (paymentId: number) => void
  closeDialogs: () => void
}

export const OrderContext = createContext<TOrderContext | undefined>(undefined)

export function OrderContextProvider({ children }: { children: React.ReactNode }) {
  const params = useParams<{ rfqId: string }>()
  const [editPaymentId, setEditPaymentId] = useState<number | undefined>(undefined)

  if (!params.rfqId) {
    throw new Error('RFQ ID is required')
  }
  const rfqId = Number.parseInt(params.rfqId, 10)

  const [pageQueryResult, refreshPageQuery] = useQuery({
    query: graphql(/* GraphQL */ `
      query OrderContextProvider($rfqId: Int!) {
        Rfq(id: $rfqId) {
          id
          name
          status
          note
          dateCreated
          dateModified
          dateReceived
          dateClosed
          project {
            id
            name
          }
          client {
            id
            name
            website
          }
          vendor {
            id
            name
            website
            commissionPercentage
          }
          quote {
            id
            quoteNumber
            amount
            dateCreated
            dateModified
            dateSent
            designFirm
            purchaseOrder {
              id
              dateCreated
              dateReceived
              dateEstimatedShipment
              dateRequestedShipment
              dateClosed
              amount
              commissionPercentage
              confirmationNumber
              poNumber
              status
              payments {
                id
                amount
                dateCreated
                dateReceived
              }
            }
          }
        }
      }
    `),
    variables: { rfqId },
    requestPolicy: 'network-only',
  })

  const [dialogOpen, setDialogOpen] = useState<TOrderContext['dialogOpen']>(undefined)

  const editingPayment = useMemo(() => {
    return pageQueryResult.data?.Rfq?.quote?.purchaseOrder?.payments.find((p) => p.id === editPaymentId)
  }, [editPaymentId, pageQueryResult.data])

  const openPaymentDialog = () => {
    setDialogOpen('addPayment')
  }

  const openEditPaymentDialog = (paymentId: number) => {
    setEditPaymentId(paymentId)
    setDialogOpen('editPayment')
  }

  const closeDialogs = () => {
    setEditPaymentId(undefined)
    setDialogOpen(undefined)
  }

  const value: TOrderContext = {
    fetching: pageQueryResult.fetching,
    rfq: pageQueryResult.data?.Rfq,
    error: pageQueryResult.error,
    refetch: refreshPageQuery,
    dialogOpen,
    editingPayment,
    openEditPaymentDialog,
    openPaymentDialog,
    closeDialogs,
  }

  useEffect(() => {
    if (value.error) {
      log('Error loading RFQ', value.error)
    }
  }, [value.error])

  /************ RFQ Logger ***********/
  useEffect(() => {
    if (value.rfq) {
      log('Rfq loaded', value.rfq)
    }
  }, [value.rfq])

  if (value.error) {
    if (value.error.message.toLowerCase().includes('not found')) {
      return <NotFound title="RFQ Not Found" />
    }
    return (
      <div>
        <Typography variant="h1" pb={4}>
          Error loading RFQ
        </Typography>
        <Alert severity="error">
          <AlertTitle>{value.error.message}</AlertTitle>
          <pre>{value.error.stack}</pre>
        </Alert>
      </div>
    )
  }

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
}

export function useOrderContext() {
  const context = useContext(OrderContext)
  if (!context) {
    throw new Error('useOrderContext must be used within an OrderContextProvider')
  }
  return context
}
